if (!customElements.get('pdp-craft-slider')) {
  customElements.define('pdp-craft-slider', class PdpCraftSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.craftObserved === 'true') return;
      this.dataset.craftObserved = 'true';

      this.timer = null;
      this.pointerStart = null;
      this.isActive = false;
      this.isVisible = false;
      this.intersectionObserver = null;
      this.placeholder = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

      this.images = Array.from(this.querySelectorAll('img'));
      this.images.forEach((image) => this.parkImage(image));

      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        this.activate();
        this.setVisible(true);
        return;
      }

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;

          const visible = entry.isIntersecting;
          this.setVisible(visible);

          if (visible && !this.isActive) {
            this.activate();
          }
        });
      }, {
        rootMargin: '100px 0px',
        threshold: 0.01
      });

      this.intersectionObserver.observe(this);
    }

    parkImage(image) {
      if (!image || image.dataset.craftParked === 'true') return;

      const src = image.getAttribute('src');
      const srcset = image.getAttribute('srcset');
      const sizes = image.getAttribute('sizes');

      if (!image.dataset.craftSrc && src && !src.startsWith('data:image/')) image.dataset.craftSrc = src;
      if (!image.dataset.craftSrcset && srcset) image.dataset.craftSrcset = srcset;
      if (!image.dataset.craftSizes && sizes) image.dataset.craftSizes = sizes;

      image.dataset.craftParked = 'true';
      image.loading = 'lazy';
      image.decoding = 'async';
      if ('fetchPriority' in image) image.fetchPriority = 'low';

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', this.placeholder);
    }

    hydrateImage(image, isThumbnail = false) {
      if (!image || image.dataset.craftHydrated === 'true') return;

      if (image.dataset.craftSrcset) image.setAttribute('srcset', image.dataset.craftSrcset);
      if (image.dataset.craftSizes) {
        image.setAttribute(
          'sizes',
          isThumbnail
            ? image.dataset.craftSizes
            : '(min-width: 1320px) 610px, (min-width: 990px) calc(50vw - 64px), calc(100vw - 40px)'
        );
      }
      if (image.dataset.craftSrc) image.setAttribute('src', image.dataset.craftSrc);

      image.dataset.craftHydrated = 'true';
    }

    hydrateSlide(index) {
      const slide = this.slides?.[index];
      if (!slide) return;
      this.hydrateImage(slide.querySelector('.pdp-craftmanship__image'));
    }

    hydrateThumbnails() {
      this.thumbnails?.forEach((thumbnail) => {
        this.hydrateImage(thumbnail.querySelector('.pdp-craftmanship__thumbnail-image'), true);
      });
    }

    activate() {
      if (this.isActive) return;
      this.isActive = true;
      this.dataset.craftReady = 'true';

      this.slides = Array.from(this.querySelectorAll('[data-craft-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-craft-thumbnail]'));
      this.previousButton = this.querySelector('[data-craft-previous]');
      this.nextButton = this.querySelector('[data-craft-next]');
      this.currentLabel = this.querySelector('[data-craft-current]');
      this.progressBar = this.querySelector('[data-craft-progress]');
      this.viewport = this.querySelector('[data-craft-viewport]');
      this.index = Math.max(this.slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(Number(this.dataset.interval || 5000), 2000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.hydrateSlide(this.index);
      this.hydrateThumbnails();

      this.onPrevious = () => this.goTo(this.index - 1, true);
      this.onNext = () => this.goTo(this.index + 1, true);
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.onPrevious();
        if (event.key === 'ArrowRight') this.onNext();
      };
      this.onPointerDown = (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
      };
      this.onPointerUp = (event) => {
        if (!this.pointerStart) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        deltaX < 0 ? this.onNext() : this.onPrevious();
      };
      this.onMouseEnter = () => this.stopAutoplay();
      this.onMouseLeave = () => this.startAutoplay();
      this.onVisibilityChange = () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      };

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.addEventListener('keydown', this.onKeydown);
      this.viewport?.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport?.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.goTo(index, true));
      });

      if (this.slides.length < 2) this.classList.add('is-single-slide');
      this.render(false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.stopAutoplay();

      if (!this.isActive) return;

      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.removeEventListener('keydown', this.onKeydown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    setVisible(visible) {
      this.isVisible = visible;
      if (!visible) {
        this.stopAutoplay();
        return;
      }

      if (this.isActive) this.startAutoplay();
    }

    goTo(nextIndex, userInitiated = false) {
      if (!this.slides?.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;
      this.hydrateSlide(this.index);
      this.render(true);
      if (userInitiated) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateViewportRatio() {
      if (!this.viewport) return;
      const activeSlide = this.slides[this.index];
      const image = activeSlide?.querySelector('.pdp-craftmanship__image');
      if (!image) return;

      const applyRatio = () => {
        if (!image.naturalWidth || !image.naturalHeight) return;
        this.viewport.style.aspectRatio = `${image.naturalWidth} / ${image.naturalHeight}`;
      };

      if (image.complete && image.dataset.craftHydrated === 'true') applyRatio();
      else image.addEventListener('load', applyRatio, { once: true });
    }

    render(animate = true) {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
        if (!animate) slide.classList.add('is-initialized');
      });

      this.thumbnails.forEach((thumbnail, index) => {
        const active = index === this.index;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.updateViewportRatio();

      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
      if (this.progressBar) this.progressBar.style.width = `${((this.index + 1) / Math.max(this.slides.length, 1)) * 100}%`;
    }

    startAutoplay() {
      if (!this.isActive || !this.isVisible || !this.autoplay || this.reducedMotion || this.slides.length < 2 || document.hidden) return;
      this.stopAutoplay();
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  });
}